import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { setProperty, deepCopy, isEmpty } from "../../util/util";
import { errorNotification } from "../../Generic/Notifications";
import Dropdown from "../../Generic/Dropdown";
import InputBox from "../../Generic/InputBox";
import Button from "../../Generic/Button";
import SuccessButton from "../../Generic/SuccessButton";
import { CSSTransition } from "react-transition-group";
import close from "../../Assets/Close.png";
import { SearchBarWithResults } from "./SearchBarWithResults";
import Checkbox from "../../Generic/Checkbox";
import { INGREDIENT_SERVING_STATES, INGREDIENT_SERVING_STATE_NAME_MAP } from "../Utils/ingredientServingState";
import {
	getSortedSegmentKeys,
	validateServingTemperatureOrder,
	validateSegmentsServingTemperatureOrder,
	reorder,
	reindexAllIngredientPositionsGlobally,
	sortSegmentsEntriesByPosition,
	reindexSegmentPositions,
} from "../Utils/RecipeUtil";
import { zIndex } from "../../config/zIndex";

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	transition: 0.1s background ease-in-out;
	border-radius: ${(props) => props.borderRadius};
	top: ${(props) => props.top};
	bottom: ${(props) => props.bottom};
	left: ${(props) => props.left};
	right: ${(props) => props.right};
	transition: 0.6s background ease-in-out;
	z-index: ${(props) => props.zIndex};
	gap: ${(props) => props.gap};
	border: ${(props) => props.border};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	height: max-content;
	width: 60%;
	border-radius: 10px;
	z-index: ${zIndex.modal};
	overflow-x: hidden;
`;

const H2 = styled.div`
	font-weight: 400;
	font-size: 18px;
`;

const H3 = styled.div`
	font-weight: 400;
	font-size: 14px;
	color: rgba(0, 0, 0, 0.5);
`;

const Block = styled(Div)`
	flex-direction: column;
	gap: 0.5rem;
`;

const SEGMENT_KEY_PREFIX = "seg_";
const SEGMENT_NAME_PREFIX = "Segment ";

const MODES = {
	ADD: "ADD",
	UPDATE: "UPDATE",
};

const servingTempDropdownOptions = [
	{ head: INGREDIENT_SERVING_STATE_NAME_MAP[INGREDIENT_SERVING_STATES.HOT], value: INGREDIENT_SERVING_STATES.HOT },
	{ head: INGREDIENT_SERVING_STATE_NAME_MAP[INGREDIENT_SERVING_STATES.COLD], value: INGREDIENT_SERVING_STATES.COLD },
];

const getInitialModalData = () => ({ selectionType: { isMandatory: false, isDefault: false } });

export const RecipeSegmentsEdit = (props) => {
	const {
		updatePayload,
		segments: segmentsProp,
		initialSegments,
		segmentsRef,
		segmentsSyncKey,
		Patterns,
	} = props;
	const initial = initialSegments ?? segmentsProp ?? {};
	const [segments, setSegments] = useState(initial);
	const [addToSegment, setAddToSegment] = useState("");
	const [modal, showModal] = useState(false);
	const [modalData, setModalData] = useState({});
	const [mode, setMode] = useState(null);
	const patterns = Object.entries(Patterns).map((item) => ({
		head: item[0],
		value: item[1],
	}));
	const changesMade = useRef(false);

	const applySegments = (next) => {
		setSegments(next);
		if (segmentsRef) segmentsRef.current = next;
	};

	useEffect(() => {
		const next = initialSegments ?? segmentsProp ?? {};
		setSegments(next);
		if (segmentsRef) segmentsRef.current = next;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segmentsSyncKey]);

	useEffect(() => {
		if (segmentsRef) return;
		if (changesMade.current) {
			updatePayload({ column: "segments", value: segments });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segments]);

	function reorderIngredientsBasedOnPosition(ingredients) {
		return Object.entries(ingredients)
			.sort(([, { position: position1 }], [, { position: position2 }]) => position1 - position2)
			.reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
	}

	/**
	 * Sort ingredients by their position property, handling edge cases
	 * 
	 * @param {Object} ingredients - Object with ingredient IDs as keys
	 * @returns {Array} Array of [ingredientId, ingredientObject] tuples sorted by position
	 * 
	 * Sorting logic:
	 * - Ingredients with valid numeric positions sorted numerically (1, 2, 3...)
	 * - Ingredients with invalid/missing positions moved to end
	 * - Handles both number and string position values
	 */
	function sortIngredientsArrayByPosition(ingredients) {
		return Object.entries(ingredients || {})
			.sort(([, a], [, b]) => {
				// Convert positions to numbers, handling string and number types
				const pa = typeof a.position === "number" ? a.position : Number(a.position);
				const pb = typeof b.position === "number" ? b.position : Number(b.position);

				// Handle invalid positions: NaN ingredients go to end
				if (isNaN(pa) && isNaN(pb)) return 0;  // Both invalid: maintain relative order
				if (isNaN(pa)) return 1;               // a invalid: a goes after b
				if (isNaN(pb)) return -1;              // b invalid: a goes before b

				// Both valid: numerical sort
				return pa - pb;
			});
	}

	/**
	 * Reindex ingredient positions within a single segment (1-based numbering)
	 * 
	 * @param {Object} mutIngredientsObj - Mutable ingredients object to modify in place
	 * 
	 * Process:
	 * 1. Sort ingredients by current position
	 * 2. Reassign positions as 1, 2, 3... within the segment
	 * 3. Modify object in place (mutation)
	 * 
	 * Used after removing ingredients to eliminate gaps in numbering
	 */
	function reindexIngredientPositions(mutIngredientsObj) {
		const ordered = sortIngredientsArrayByPosition(mutIngredientsObj);
		ordered.forEach(([id, obj], idx) => {
			// Assign 1-based position within this segment
			mutIngredientsObj[id] = { ...obj, position: idx + 1 };
		});
	}

	function removeSegment(segmentId) {
		let newSegments = deepCopy(segments);
		delete newSegments[segmentId];
		newSegments = reindexSegmentPositions(newSegments);
		reindexAllIngredientPositionsGlobally(newSegments);
		applySegments(newSegments);
		changesMade.current = true;
	}

	function removeIngredient(segmentId, ingredientId) {
		let newSegments = deepCopy(segments);
		//Get the segment to edit
		delete newSegments[segmentId]["ingredients"][ingredientId];
		reindexAllIngredientPositionsGlobally(newSegments);
		applySegments(newSegments);
		changesMade.current = true;
	}

	/**
	 * Handle the end of a drag operation for both segments and ingredients
	 * 
	 * This function is called by react-beautiful-dnd when a drag operation completes.
	 * It handles two types of drags:
	 * 1. SEGMENT: Reordering segments in the main list
	 * 2. INGREDIENT: Moving ingredients within or between segments (with temperature validation)
	 * 
	 * @param {Object} result - Drag result object from react-beautiful-dnd
	 * @param {Object} result.source - Source location { droppableId, index }
	 * @param {Object} result.destination - Destination location { droppableId, index } 
	 * @param {string} result.draggableId - ID of the dragged item
	 * @param {string} result.type - "SEGMENT" or "INGREDIENT"
	 */
	function onDragEnd(result) {

		if (!result.destination) {
			return;
		}

		if (result.type === "SEGMENT") {
			if (result.destination.index === result.source.index) return;
			const orderedSegments = reorder(segments, result.source.index, result.destination.index);
			try {
				validateSegmentsServingTemperatureOrder(orderedSegments);
			} catch (e) {
				errorNotification(`Could not reorder segment: ${e.message}`);
				return;
			}
			changesMade.current = true;
			let newSegments = deepCopy(orderedSegments);
			reindexAllIngredientPositionsGlobally(newSegments); // Maintain global position order
			applySegments(newSegments);
			return;
		}

		if (result.type === "INGREDIENT") {
			const sourceDroppableId = result.source.droppableId; // INGREDIENT_<segKey>
			const destinationDroppableId = result.destination.droppableId;
			const sourceSegmentKey = sourceDroppableId.replace("INGREDIENT_", "");
			const destinationSegmentKey = destinationDroppableId.replace("INGREDIENT_", "");
			const destinationIndex = result.destination.index;

			// Parse dragged ingredient ID from draggableId format: "ING-<segKey>-<ingredientId>"
			const draggableId = result.draggableId;
			const ingredientId = draggableId.split("-").pop();

			let newSegments = deepCopy(segments);
			const sourceIngredientsObj = newSegments[sourceSegmentKey]?.ingredients || {};
			const destIngredientsObj = newSegments[destinationSegmentKey]?.ingredients || {};

			// Build ordered arrays of ingredients sorted by position
			let sourceArr = sortIngredientsArrayByPosition(sourceIngredientsObj);
			let destArr = sourceSegmentKey === destinationSegmentKey
				? [...sourceArr]  // Same segment: use copy of source array
				: sortIngredientsArrayByPosition(destIngredientsObj); // Different segment: use destination array

			// Find the dragged ingredient
			const draggedEntry = sourceArr.find(([id]) => id === ingredientId);
			if (!draggedEntry) return;
			const draggedIngredient = draggedEntry[1];

			// Remove dragged ingredient from arrays to calculate correct neighbor positions
			// This is crucial for accurate "where will this land?" calculations
			const removeFrom = (arr) => arr.filter(([id]) => id !== ingredientId);
			sourceArr = removeFrom(sourceArr);
			if (sourceSegmentKey === destinationSegmentKey) destArr = removeFrom(destArr);

			// Calculate what ingredients will be before/after the dropped ingredient
			const prevTuple = destinationIndex > 0 ? destArr[destinationIndex - 1] : null;
			const nextTuple = destinationIndex < destArr.length ? destArr[destinationIndex] : null;
			const prevIngredient = prevTuple ? prevTuple[1] : null;
			const nextIngredient = nextTuple ? nextTuple[1] : null;

			const targetTemp = draggedIngredient?.servingTemp;
			try {
				validateServingTemperatureOrder(targetTemp, prevIngredient, nextIngredient);
			} catch (e) {
				errorNotification(`Could not drop ${draggedIngredient?.name || ingredientId}: ${e.message}`);
				return; // Reject the drop - ingredient stays in original position
			}

			changesMade.current = true;

			// Perform move
			if (sourceSegmentKey === destinationSegmentKey) {
				// SAME SEGMENT: Reorder within segment
				const workingArr = destArr;
				workingArr.splice(destinationIndex, 0, [ingredientId, draggedIngredient]);

				// Rebuild ingredients object with new positions (1-based within segment)
				const rebuilt = {};
				workingArr.forEach(([id, obj], idx) => {
					rebuilt[id] = { ...obj, position: idx + 1 };
				});
				newSegments[sourceSegmentKey].ingredients = rebuilt;

			} else {
				// DIFFERENT SEGMENT: Move between segments

				// Remove from source segment
				delete newSegments[sourceSegmentKey].ingredients[ingredientId];
				reindexIngredientPositions(newSegments[sourceSegmentKey].ingredients);

				// Insert into destination segment at specific index
				const workingArr = sortIngredientsArrayByPosition(newSegments[destinationSegmentKey].ingredients);
				workingArr.splice(destinationIndex, 0, [ingredientId, draggedIngredient]);

				// Rebuild destination ingredients object with new positions
				const rebuilt = {};
				workingArr.forEach(([id, obj], idx) => {
					rebuilt[id] = { ...obj, position: idx + 1 };
				});
				newSegments[destinationSegmentKey].ingredients = rebuilt;
			}

			reindexAllIngredientPositionsGlobally(newSegments);
			applySegments(newSegments);
			changesMade.current = true;
		}
	}

	function addIngredient() {
		const { _id, ingredientIdToUpdate, ...ingredientPayload } = modalData;
		const targetServingTemp = ingredientPayload.servingTemp;
		const effectiveId = mode === MODES.UPDATE ? ingredientIdToUpdate : _id;

		// Build target segment's ingredients as they would be after add/update (for validation only)
		const currentIngredients = segments[addToSegment]?.ingredients || {};
		let wouldBeIngredients = { ...currentIngredients };
		if (mode === MODES.UPDATE) {
			if (!_id) {
				wouldBeIngredients[ingredientIdToUpdate] = { ...ingredientPayload };
			} else {
				delete wouldBeIngredients[ingredientIdToUpdate];
				wouldBeIngredients[_id] = { ...ingredientPayload };
			}
		} else {
			wouldBeIngredients[_id] = { ...ingredientPayload };
		}

		const orderedInSegment = sortIngredientsArrayByPosition(wouldBeIngredients);
		const effectiveIndex = orderedInSegment.findIndex(([id]) => id === effectiveId);
		if (effectiveIndex < 0) return;

		const sortedSegmentKeys = getSortedSegmentKeys(segments);
		const segIndex = sortedSegmentKeys.indexOf(addToSegment);

		let prevIngredient = null;
		if (effectiveIndex > 0) {
			prevIngredient = orderedInSegment[effectiveIndex - 1][1];
		} else if (segIndex > 0) {
			const prevSegKey = sortedSegmentKeys[segIndex - 1];
			const prevSegIngredients = segments[prevSegKey]?.ingredients || {};
			const orderedPrev = sortIngredientsArrayByPosition(prevSegIngredients);
			if (orderedPrev.length) prevIngredient = orderedPrev[orderedPrev.length - 1][1];
		}

		let nextIngredient = null;
		if (effectiveIndex < orderedInSegment.length - 1) {
			nextIngredient = orderedInSegment[effectiveIndex + 1][1];
		} else if (segIndex >= 0 && segIndex < sortedSegmentKeys.length - 1) {
			const nextSegKey = sortedSegmentKeys[segIndex + 1];
			const nextSegIngredients = segments[nextSegKey]?.ingredients || {};
			const orderedNext = sortIngredientsArrayByPosition(nextSegIngredients);
			if (orderedNext.length) nextIngredient = orderedNext[0][1];
		}

		try {
			validateServingTemperatureOrder(targetServingTemp, prevIngredient, nextIngredient);
		} catch (e) {
			errorNotification(`Could not add ${ingredientPayload?.name || effectiveId}: ${e.message}`);
			return;
		}

		let newSegments = deepCopy(segments);
		if (mode === MODES.UPDATE) {
			if (!_id) {
				newSegments[addToSegment]["ingredients"][ingredientIdToUpdate] = { ...ingredientPayload };
			} else {
				delete newSegments[addToSegment]["ingredients"][ingredientIdToUpdate];
				newSegments[addToSegment]["ingredients"][_id] = { ...ingredientPayload };
			}
		} else {
			newSegments[addToSegment]["ingredients"][_id] = { ...ingredientPayload };
		}

		newSegments[addToSegment]["ingredients"] = reorderIngredientsBasedOnPosition(newSegments[addToSegment]["ingredients"]);
		reindexAllIngredientPositionsGlobally(newSegments);
		applySegments(newSegments);
		showModal(false);
		setModalData({});
		changesMade.current = true;
	}

	function updateSegment(col, val, segmentId) {
		changesMade.current = true;
		let newSegments = deepCopy(segments);
		setProperty(newSegments[segmentId], val, col);
		applySegments(newSegments);
	}

	function addSegment() {
		changesMade.current = true;

		const newSegments = deepCopy(segments);
		const count = Object.keys(newSegments).length;
		const nextIndex = count + 1;
		newSegments[SEGMENT_KEY_PREFIX + nextIndex] = {
			name: SEGMENT_NAME_PREFIX + nextIndex,
			position: nextIndex,
			quantity: {
				minPortion: 0,
				maxPortion: 0,
			},
			ingredients: {},
		};
		applySegments(reindexSegmentPositions(newSegments));
	}

	/**
	 * Render draggable ingredient cards for a specific segment
	 * 
	 * @param {Object} segmentData - Segment data containing ingredients
	 * @param {string} segmentKey - Unique key identifying the segment
	 * @returns {Array} Array of JSX elements (Draggable ingredient cards)
	 * 
	 * Features:
	 * - Each ingredient is wrapped in a Draggable component
	 * - Handle-only dragging (only drag icon can initiate drag)
	 * - Click to edit ingredient (opens modal)
	 * - Delete button with event propagation stop
	 * - Displays all ingredient properties in a card layout
	 */
	function renderSegmentIngredients(segmentData, segmentKey) {
		let ingredientsRenderData = [];
		const { ingredients } = segmentData;

		function handleOnClick(ingredientId) {
			setAddToSegment(segmentKey);
			setMode(MODES.UPDATE);
			const payload = deepCopy(ingredients[ingredientId]);
			setModalData({ ...getInitialModalData(), ...payload, ingredientIdToUpdate: ingredientId });
			showModal(true);
		}

		if (!isEmpty(ingredients)) {
			// Use same sorting order as drag-and-drop logic to ensure consistent indices
			const sortedIngredients = sortIngredientsArrayByPosition(ingredients);
			sortedIngredients.forEach(([ingredientId, ingredient], index) => {
				const { selectionType, quantity, pattern, servingTemp, amountPerPortion, position, name } = ingredient;

				// Look up pattern display name from Patterns
				const PatternDetails = Object.entries(Patterns).find(([key, item]) => {
					if (item === pattern?.id) return true;
					return false;
				});

				ingredientsRenderData.push(
					// Draggable wrapper with unique ID format: ING-<segmentKey>-<ingredientId>
					<Draggable key={`ING-${segmentKey}-${ingredientId}`} draggableId={`ING-${segmentKey}-${ingredientId}`} index={index}>
						{(provided, snapshot) => (
							<Div
								ref={provided.innerRef}
								{...provided.draggableProps}
								onClick={() => handleOnClick(ingredientId)}
								flexDirection="column"
								padding="1rem"
								gap="1rem"
								border="1px solid rgba(0, 0, 0, 0.2)"
								borderRadius="10px"
								// Preserve drag transform styles from react-beautiful-dnd
								style={{ ...(provided.draggableProps && provided.draggableProps.style ? provided.draggableProps.style : {}) }}>
								<Div jContent="space-between" width="100%" alignItem="center">
									<Div flexDirection="row" alignItem="center" gap="0.5rem">
										{/* DRAG HANDLE - Only this icon can start drag */}
										<span
											className="material-symbols-outlined"
											style={{ cursor: "grab" }}
											{...provided.dragHandleProps}
											onClick={(e) => e.stopPropagation()} // Prevent modal open on drag handle click
										>
											drag_indicator
										</span>
										<H2>{name || ingredientId}</H2>
									</Div>
									<Div
										onClick={(e) => {
											e.stopPropagation(); // Prevent modal open on delete click
											removeIngredient(segmentKey, ingredientId);
										}}>
										<span className="material-symbols-outlined">delete</span>
									</Div>
								</Div>
								<Div flexDirection="row" gap="1rem">
									<Block>
										<H3>Default</H3> {selectionType?.isDefault ? "Yes" : "No"}
									</Block>
									<Block>
										<H3>Mandatory</H3> {selectionType?.isMandatory ? "Yes" : "No"}
									</Block>
								</Div>
								<Div flexDirection="row" gap="1rem">
									<Block>
										<H3>Min Portions</H3> {quantity?.minPortion ?? "-"}
									</Block>
									<Block>
										<H3>Max Portions</H3> {quantity?.maxPortion ?? "-"}
									</Block>
									<Block>
										<H3>Default Portions</H3> {quantity?.defaultPortion ?? "-"}
									</Block>
								</Div>
								<Div flexDirection="row" gap="1rem">
									<Block>
										<H3>Pattern</H3> {PatternDetails?.[0] ?? "-"}
									</Block>
									<Block>
										<H3>Amount Per Portion</H3> {amountPerPortion ?? "-"}
									</Block>
									<Block>
										<H3>Position</H3> {position ?? "-"}
									</Block>
									<Block>
										<H3>Serving State</H3> {servingTemp ? INGREDIENT_SERVING_STATE_NAME_MAP[servingTemp] ?? servingTemp : "-"}
									</Block>
								</Div>
							</Div>
						)}
					</Draggable>
				);
			});
		}

		return ingredientsRenderData;
	}

	return (
		<>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="SEGMENT_LIST" type="SEGMENT">
					{(provided) => (
						<Div
							borderRadius="20px"
							boxShadow=""
							padding="0"
							margin="1rem 0 0 0"
							width="100%"
							flexDirection="column"
							ref={provided.innerRef}>
							<Div width="100%" jContent="flex-end">
								<Button active margin="0rem 0 0 0" onClick={() => addSegment()}>
									Add Segment
								</Button>
							</Div>
							{segments ? (
								sortSegmentsEntriesByPosition(segments).map(([key, item], index) => (
									<Draggable key={key} draggableId={key} index={index}>
										{(provided) => (
											<Div
												margin="1rem 0 0 0"
												background="#fff"
												borderRadius="10px"
												alignItem="center"
												width="100%"
												padding="1rem"
												gap="1rem"
												flexDirection="column"
												ref={provided.innerRef}
												{...provided.draggableProps}>
												<Div width="100%" flexDirection="row" jContent="space-between" alignItem="center" gap="1rem">
													<Div flexDirection="row" alignItem="center" gap="0.5rem">
														<span className="material-symbols-outlined" style={{ cursor: "grab" }} {...provided.dragHandleProps}>drag_indicator</span>
														<H2>{item?.name || key}</H2>
													</Div>
													<Div onClick={() => removeSegment(key)}>
														<span className="material-symbols-outlined">delete</span>
													</Div>
												</Div>

												<Div width="100%" flexDirection="row" gap="1rem" jContent="" alignItem="">
													<Div flexDirection="column" jContent="" alignItem="">
														<Div color="rgba(0,0,0,0.6)">Name</Div>
														<Div width="20rem" margin="0.5rem 0 0 0">
															<InputBox
																id={"name"}
																onChange={(e) => {
																	updateSegment("name", e.target.value, key);
																}}
																value={item?.name}
																padding="0.5rem"
																width="100%"
															/>
														</Div>
													</Div>

													<Div flexDirection="column" jContent="" alignItem="">
														<Div color="rgba(0,0,0,0.6)">Min Portion</Div>
														<Div width="20rem" margin="0.5rem 0 0 0">
															<InputBox
																id={"name"}
																onChange={(e) => {
																	updateSegment("quantity.minPortion", e.target.valueAsNumber, key);
																}}
																type="number"
																value={item?.quantity?.minPortion}
																padding="0.5rem"
																width="100%"
															/>
														</Div>
													</Div>

													<Div flexDirection="column" jContent="" alignItem="">
														<Div color="rgba(0,0,0,0.6)">Max Portion</Div>
														<Div width="20rem" margin="0.5rem 0 0 0">
															<InputBox
																id={"name"}
																onChange={(e) => {
																	updateSegment("quantity.maxPortion", e.target.valueAsNumber, key);
																}}
																value={item?.quantity?.maxPortion}
																padding="0.5rem"
																width="100%"
																type="number"
															/>
														</Div>
													</Div>
												</Div>

												<Div width="100%" alignItem="center" flexDirection="row" jContent="space-between" gap="1rem">
													<H2>Ingredients</H2>
													<Button
														active
														margin="0rem 0 0 0"
														onClick={() => {
															showModal(true);
															setAddToSegment(key);
															setModalData(getInitialModalData());
															setMode(MODES.ADD);
														}}>
														Add Ingredient
													</Button>
												</Div>

												<Droppable droppableId={`INGREDIENT_${key}`} type="INGREDIENT" direction="vertical">
													{(ingProvided) => (
														<Div
															width="100%"
															flexDirection="column"
															padding="0"
															gap="1rem"
															ref={ingProvided.innerRef}
														>
															{renderSegmentIngredients(item, key)}
															{ingProvided.placeholder}
														</Div>
													)}
												</Droppable>
											</Div>
										)}
									</Draggable>
								))
							) : (
								<Div width="100%" padding="2rem" jContent="center" borderRadius="10px" background="rgba(45, 35, 66, 0.1)">
									No Segments
								</Div>
							)}
							{/* List space placeholder*/}
							{provided.placeholder}
						</Div>
					)}
				</Droppable>
				{/*Modal for ingredient add */}
				<CSSTransition in={modal} timeout={1000} classNames="container" unmountOnExit>
					<Overlay>
						<Modal>
							<Div
								jContent="space-between"
								alignItem="center"
								color="#fff"
								width="100%"
								background="rgba(45, 35, 66, 0.8)"
								fontSize="1.7rem"
								padding="1rem">
								<Span>
									{mode === MODES.ADD ? "Add " : "Update "}
									Ingredient
								</Span>
								<img
									onClick={() => {
										showModal(false);
										setModalData({});
									}}
									src={close}
									alt="close"
									style={{
										height: "30px",
										filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
									}}
								/>
							</Div>

							<Div flexDirection="row" padding="1rem" flexWrap="wrap" gap="1rem" alignItem="flex-end">
								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Ingredient</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<SearchBarWithResults
											colToShow="name"
											colToStore={["_id", "name"]}
											requestType="post"
											endpoint="/v3/dashboard/ingredients/search?page=1&"
											initialQuery={modalData?.name}
											multiKeyToStore
											onChange={(val) => setModalData({ ...modalData, ...val })}
										/>
									</Div>
								</Div>

								<Div height="100%" margin="0 0 0.5rem 0">
									<Checkbox
										defaultChecked={modalData?.selectionType?.isDefault}
										onClick={(e) =>
											setModalData((prev) => {
												setProperty(prev, e.target.checked, "selectionType.isDefault");
												return prev;
											})
										}
									/>
									<Span margin="0 0 0 0.5rem">Default</Span>
								</Div>
								<Div height="100%" margin="0 0 0.5rem 0">
									<Checkbox
										defaultChecked={modalData?.selectionType?.isMandatory}
										onClick={(e) =>
											setModalData((prev) => {
												setProperty(prev, e.target.checked, "selectionType.isMandatory");
												return prev;
											})
										}
									/>
									<Span margin="0 0 0 0.5rem">Mandatory</Span>
								</Div>
							</Div>

							<Div flexDirection="row" padding="0 1rem" flexWrap="wrap" gap="1rem">
								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Min Portion</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<InputBox
											id={"quantity.minPortion"}
											onChange={(e) =>
												setModalData((prev) => {
													setProperty(prev, e.target.valueAsNumber, "quantity.minPortion");
													return prev;
												})
											}
											type="number"
											padding="0.5rem"
											width="100%"
											defaultValue={modalData?.quantity?.minPortion}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Max Portion</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<InputBox
											id={"quantity.maxPortion"}
											onChange={(e) =>
												setModalData((prev) => {
													setProperty(prev, e.target.valueAsNumber, "quantity.maxPortion");
													return prev;
												})
											}
											padding="0.5rem"
											width="100%"
											type="number"
											defaultValue={modalData?.quantity?.maxPortion}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Default Portion</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<InputBox
											id={"quantity.defaultPortion"}
											onChange={(e) =>
												setModalData((prev) => {
													setProperty(prev, e.target.valueAsNumber, "quantity.defaultPortion");
													return prev;
												})
											}
											padding="0.5rem"
											width="100%"
											type="number"
											defaultValue={modalData?.quantity?.defaultPortion}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Patterns</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<Dropdown
											headerFontSize="1rem"
											margin="0 1rem 0 0"
											width="100%"
											boxShadow=""
											border="1px solid rgba(45, 35, 66, 0.5)"
											value={modalData?.pattern?.id}
											optionsList={patterns}
											onChange={(val) =>
												setModalData((prev) => {
													let newModalData = deepCopy(prev);
													setProperty(newModalData, val, "pattern.id");
													return newModalData;
												})
											}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Amount Per Portion</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<InputBox
											id={"amountPerPortion"}
											onChange={(e) =>
												setModalData((prev) => {
													setProperty(prev, e.target.valueAsNumber, "amountPerPortion");
													return prev;
												})
											}
											padding="0.5rem"
											width="100%"
											type="number"
											defaultValue={modalData?.amountPerPortion}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Position</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<InputBox
											id={"position"}
											onChange={(e) =>
												setModalData((prev) => {
													setProperty(prev, e.target.valueAsNumber, "position");
													return prev;
												})
											}
											padding="0.5rem"
											width="100%"
											type="number"
											defaultValue={modalData?.position}
										/>
									</Div>
								</Div>

								<Div flexDirection="column" jContent="" alignItem="">
									<Div color="rgba(0,0,0,0.6)">Serving State</Div>
									<Div width="20rem" margin="0.5rem 0 0 0">
										<Dropdown
											headerFontSize="1rem"
											margin="0 1rem 0 0"
											width="100%"
											boxShadow=""
											border="1px solid rgba(45, 35, 66, 0.5)"
											value={modalData?.servingTemp}
											optionsList={servingTempDropdownOptions}
											onChange={(val) =>
												setModalData((prev) => {
													let newModalData = deepCopy(prev);
													setProperty(newModalData, val, "servingTemp");
													return newModalData;
												})
											}
										/>
									</Div>
								</Div>
							</Div>

							<Div jContent="flex-end" margin="0 0 0 0">
								<Div padding="1rem">
									<Button
										margin="0 1rem 0 0"
										active
										onClick={() => {
											showModal(false);
											setAddToSegment(null);
											setModalData({});
										}}>
										Back
									</Button>
									<SuccessButton
										onClick={() => {
											addIngredient();
										}}>
										{mode === MODES.ADD ? "Add" : "Update"}
									</SuccessButton>
								</Div>
							</Div>
						</Modal>
					</Overlay>
				</CSSTransition>
			</DragDropContext>
		</>
	);
};
